/* eslint-disable valid-jsdoc */
import _ from 'underscore';
import { defineConstProperty } from '@bingads-webui/reflection';
import * as urlUtil from '@bingads-webui/url-util';
import {
  INavigation,
  IResourceIdentifier,
  UrlJoiner,
  EdmContext,
  WithKeyNavigationParams,
  CastNavigationParams,
  ActionNavigationParams,
  NavigationStep,
  ResIDWithType,
  CastNavigationRuntime,
  WithKeyNavigationRuntime,
  CallNavigationRuntime,
} from '../types';

export function createNavigation() {
  /**
   * @class Navigation
   * @property {ResourceIdentifier} source - The source accessor navigated from
   */
  return class Navigation implements INavigation {
    source!: IResourceIdentifier;

    constructor({
      source,
    }: { source: IResourceIdentifier }) {
      defineConstProperty(this, 'source', source);
    }

    toJSON() {
      return [...this.source.toJSON(), this.toSelfJSON()];
    }

    toSelfJSON() {
      throw new Error("I'm abstract");
    }
  };
}

export function createCastNavigation(Superclass = Object, url: UrlJoiner, edm: EdmContext) {
  /**
   * @class CastNavigation
   * @extends INavigation
   * @property {Type} type - The type casting to
   * @property {String} name - The type name used for casting
   */
  // eslint-disable-next-line no-redeclare, no-unused-vars
  interface CastNavigation {
    name: string;
  }
  // eslint-disable-next-line no-redeclare, no-shadow
  return class CastNavigation extends Superclass {
    constructor({
      source,
      type,
      name = type.name,
    }: CastNavigationParams) {
      super({
        source,
      });

      (_.chain(this) as any).defineConstProperty('type', type)
        .defineConstProperty('name', name)
        .value();

      defineConstProperty(this, 'path', url.join(source.path, name));
    }

    toSelfJSON() {
      const navigation = this as this & CastNavigationRuntime;

      return {
        type: 'function',
        name: CastNavigation.navigationName,
        parameters: [navigation.name],
      };
    }

    static get navigationName() {
      return '$cast';
    }

    static defineOn(TypeResID: ResIDWithType) {
      /**
       * $cast navigation method of the TypeResID
       * @memberof TypeResID#
       * @this TypeResID
       * @param {String} name - The name of the subclass
       * @returns {TypeResID} The ResourceIdentifier to access the subclass object
       */
      function navCastMethod(name: string): IResourceIdentifier {
        const self = this as ResIDWithType;
        const entityType = edm.types.resolve(name, self.type.namespace);
        const type = TypeResID.prototype.type instanceof edm.types.CollectionType
          ? entityType.collectionType
          : entityType;
        const navigation =
          new CastNavigation({ source: self, type: entityType, name }) as
            InstanceType<typeof CastNavigation> & INavigation;

        return new type!.ResourceIdentifier!({ navigation });
      }

      defineConstProperty(TypeResID.prototype, CastNavigation.navigationName, navCastMethod);
    }
  };
}

export function createWithKeyNavigation(Superclass = Object) {
  /**
   * @class WithKeyNavigation
   * @extends INavigation
   * @property {String|Number} key - The key of the selected entity
   * @property {String|Number} ignoreKeyTypeInPath - By default false. If true, compose path following /<path>/<id>
   */
  // eslint-disable-next-line no-redeclare, no-unused-vars
  interface WithKeyNavigation {
    key: string | number;
  }
  // eslint-disable-next-line no-redeclare, no-shadow
  return class WithKeyNavigation extends Superclass {
    constructor({
      source,
      key,
      ignoreKeyTypeInPath,
    }: WithKeyNavigationParams) {
      super({
        source,
      });

      defineConstProperty(this, 'key', key);
      defineConstProperty(this, 'path', (() => {
        // In case people pass a decimal string for a integer key
        // We cannot use parseInt directly, it would get a wrong number if the
        // key is beyond Number.MAX_SAFE_INTEGER
        if (_.isString(key)) {
          if (ignoreKeyTypeInPath) {
            return urlUtil.join(source.path, key as string);
          }
          const keyTypeValue = _.chain(source)
            .result('type')
            .result('elementType')
            .result('keyProperty')
            .result('typeName')
            .value();
          const keyType = _.isString(keyTypeValue) || _.isNumber(keyTypeValue)
            ? keyTypeValue
            : undefined;

          if (keyType === 'integer') {
            if ((key as string).match(/^[+-]?(0|[1-9][0-9]*)$/)) {
              return `${source.path}(${key})`;
            }
          }
          if (keyType === 'string') {
            return `${source.path}('${key}')`;
          }
        }

        return `${source.path}(${JSON.stringify(key)})`;
      })());
    }

    toSelfJSON() {
      const navigation = this as this & WithKeyNavigationRuntime;

      return {
        type: 'function',
        name: WithKeyNavigation.navigationName,
        parameters: [navigation.key],
      };
    }

    static get navigationName() {
      return '$withKey';
    }

    static defineOn(TypeResID: ResIDWithType) {
      /**
       * $withKey navigation method of the TypeResID
       * @memberof TypeResID#
       * @this TypeResID
       * @param {String} key - The key of the element
       * @param {Boolean} ignoreKeyTypeInPath - Decide how to add key to path
       * @returns {IResourceIdentifier}
       */
      function navWithKeyMethod(key: string | number, ignoreKeyTypeInPath = false): IResourceIdentifier {
        const self = this as ResIDWithType;
        const navigation =
          new WithKeyNavigation({ source: self, key, ignoreKeyTypeInPath }) as
            InstanceType<typeof WithKeyNavigation> & INavigation;

        return new self.type.elementType!.ResourceIdentifier({ navigation });
      }

      defineConstProperty(
        TypeResID.prototype,
        WithKeyNavigation.navigationName,
        navWithKeyMethod
      );
    }
  };
}

export function createCallNavigation(Superclass = Object, edm: EdmContext) {
  /**
   * @class CallNavigation
   * @extends INavigation
   * @property {Object} parameters - The parameters for the function call
   */
  // eslint-disable-next-line no-redeclare, no-unused-vars
  interface CallNavigation {
    parameters: Record<string, any>;
  }
  // eslint-disable-next-line no-redeclare, no-shadow
  return class CallNavigation extends Superclass {
    constructor({
      source,
      parameters = {},
    }: ActionNavigationParams) {
      super({
        source,
      });
      defineConstProperty(this, 'parameters', parameters);

      const path = source.type instanceof edm.types.ActionType
        ? source.path : `${source.path}(${_.map(parameters, (value, name) => `${name}=${value}`).join(',')})`;

      defineConstProperty(this, 'path', path);
    }

    toSelfJSON() {
      const navigation = this as this & CallNavigationRuntime;

      return {
        type: 'function',
        name: CallNavigation.navigationName,
        parameters: [navigation.parameters],
      };
    }

    static get navigationName() {
      return '$call';
    }

    static defineOn(TypeResID: ResIDWithType) {
      /**
       * $call navigation method of the TypeResID
       * @memberof TypeResID#
       * @this TypeResID
       * @param {object} parameters - The parameters to call the callable
       * @return {IResourceIdentifier}
       * The ResourceIdentifier accessing the return value of the callable
       */
      function navCallMethod(parameters: Record<string, any>): IResourceIdentifier {
        const self = this as ResIDWithType;
        const navigation =
          new CallNavigation({ source: self, parameters }) as
            InstanceType<typeof CallNavigation> & INavigation;

        return new self.type.returnType!.ResourceIdentifier({ navigation });
      }

      defineConstProperty(TypeResID.prototype, CallNavigation.navigationName, navCallMethod);
    }
  };
}

export function createResourceIdentifier() {
  /**
   * @class ResourceIdentifier
   * @property {Navigation} navigation
   * - The navigation back tracking the parent ResourceIdentifier
   */
  return class ResourceIdentifier {
    navigation?: INavigation;

    /**
     * Create a ResourceIdentifier
     * @param {Object} [options] - The constructor options
     * @param {INavigation} [options.navigation]
     * - The navigation back tracking the parent ResourceIdentifier
     * @return {Void} Nothing to return
     */
    constructor({
      navigation,
    }: { navigation?: INavigation } = {}) {
      if (navigation) {
        defineConstProperty(this, 'navigation', navigation);
        defineConstProperty(this, 'path', navigation.path);
      } else {
        defineConstProperty(this, 'path', '');
      }
    }

    toJSON() {
      if (this.navigation) {
        return this.navigation.toJSON();
      }

      return [];
    }

    identifyEntitySet(json: NavigationStep[]): ResourceIdentifier | null {
      // eslint-disable-next-line
      let entitySet: any = this;

      /* eslint guard-for-in: 0 */
      /* eslint no-restricted-syntax: 0 */
      for (const i in json) {
        const item = json[i];

        if (item.type === 'property') {
          entitySet = entitySet[item.name];
        } else if (item.type === 'function') {
          entitySet = entitySet[item.name](...item.parameters!);
        }

        if (!entitySet) {
          return null;
        }
      }

      return entitySet;
    }
  };
}
